import React, {useCallback, useLayoutEffect, useMemo, useState} from 'react';
import ThemeProvider from 'theme/ThemeProvider';
import {useGetBrandingQuery} from 'theme/store/themeApi';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {
    Branding,
    availableSet,
    brandingSet,
    selectManufacturerFavicon,
    selectManufacturerName,
} from 'store/customer/brandingSlice';
import {shallowEqual} from 'react-redux';
import {
    isDeviceMedium,
    isDeviceSmall,
    isDeviceSmallMedium,
} from 'shared/helpers/DeviceSize';
import {setTitleAndBranding as setBranding} from 'theme/helper';
import {produce} from 'immer';
import {baseTheme} from 'theme';
import {getBrandedTheme} from 'theme/helper/getBrandedTheme';

/*
 * Extract the room and cabinet slugs from the current URL path as we cannot use route as theme is higher than route providers
 * Looks for 'preview-3drp' for the room slug and 'preview-3d' for the cabinet slug.
 *
 * @return {{ roomSlug?: string; cabinetSlug?: string }} An object containing the extracted slugs.
 */
const getPreviewSlugsFromPath = (): {
    roomSlug?: string;
    cabinetSlug?: string;
} => {
    // Split pathname into clean segments (removes empty strings from slashes)
    const segments = window.location.pathname.split('/').filter(Boolean);

    // Find the index positions of route keys
    const roomKeyIndex = segments.indexOf('preview-3drp');
    const cabinetKeyIndex = segments.indexOf('preview-3d');

    // Safely grab the value directly following the key segment
    const roomSlug =
        roomKeyIndex !== -1 ? segments[roomKeyIndex + 1] : undefined;
    const cabinetSlug =
        cabinetKeyIndex !== -1 ? segments[cabinetKeyIndex + 1] : undefined;

    return {roomSlug, cabinetSlug};
};

const themeProviderWithCustomerApi = () => {
    const Provider = ({children}: {children: React.ReactNode}) => {
        const dispatch = useAppDispatch();
        const manufacturer = useAppSelector(
            selectManufacturerName,
            shallowEqual
        );

        const [theme, setTheme] = useState(baseTheme);
        const [isCustomFont, setIsCustomFont] = useState(false);

        const favicon = useAppSelector(selectManufacturerFavicon, shallowEqual);

        // When on the preview pages, pass the slug to branding API so it
        // resolves manufacturer/customer branding details instead of subdomain
        const {cabinetSlug, roomSlug} = useMemo(
            () => getPreviewSlugsFromPath(),
            []
        );
        const {isLoading, isUninitialized, data} = useGetBrandingQuery({
            cabinetSlug,
            roomSlug,
        });

        const isSmallDevice = isDeviceSmall();
        const isMediumDevice = isDeviceMedium();
        const isSmallMediumDevice = isDeviceSmallMedium();

        const setTitleAndBranding = useCallback(
            ({available, branding, colours}: typeof data) => {
                const nameAndTitle: Branding = {
                    branding_manufacturer_name: manufacturer,
                    branding_manufacturer_favicon: favicon,
                };

                if (available) {
                    if (branding.branding_manufacturer_name)
                        nameAndTitle.branding_manufacturer_name =
                            branding.branding_manufacturer_name;

                    if (branding.branding_manufacturer_favicon)
                        nameAndTitle.branding_manufacturer_favicon =
                            branding.branding_manufacturer_favicon;
                }

                setBranding(
                    nameAndTitle,
                    available ? colours : undefined,
                    Boolean(cabinetSlug)
                );
            },
            [manufacturer, favicon, cabinetSlug]
        );

        useLayoutEffect(() => {
            setTheme(
                produce((draft) => {
                    draft.device.isSmall = isSmallDevice;
                    draft.device.isMedium = isMediumDevice;
                    draft.device.isSmallMedium = isSmallMediumDevice;
                })
            );
        }, [isSmallDevice, isMediumDevice, isSmallMediumDevice]);

        useLayoutEffect(() => {
            setTitleAndBranding(data ? data : {available: false});
            if (data) {
                if (!data.available) {
                    dispatch(availableSet(false));
                    return;
                }

                dispatch(brandingSet(data.branding));

                setTheme(() => {
                    let updatedTheme = getBrandedTheme(data, baseTheme);

                    if (data?.branding?.background_image) {
                        updatedTheme = produce(updatedTheme, (draft) => {
                            draft.images.loginBackground =
                                data.branding.background_image;
                        });
                    }
                    return updatedTheme;
                });

                if (data?.branding?.is_custom_font) {
                    setIsCustomFont(true);
                }
            }
        }, [data]);

        return (
            <ThemeProvider
                theme={theme}
                isLoading={isLoading || isUninitialized}
                isCustomFont={isCustomFont}>
                {children}
            </ThemeProvider>
        );
    };

    return Provider;
};

export default themeProviderWithCustomerApi();
